import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Palette as PaletteIcon } from 'lucide-react';
import { ShieldCheck, Mail, Check, X, Trash2, Loader2, Crown, UserCog, User, Clock, Inbox } from 'lucide-react';
import { Header } from '../components/Header';
import { LogoUpload } from '../components/LogoUpload';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import api from '../lib/api';

const statusStyle = { active: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700', blocked: 'bg-red-100 text-red-700' };
const roleIcon = { owner: Crown, admin: UserCog, member: User };

export default function Admin() {
  const { user } = useAuth();
  const { brand, refresh: refreshBrand } = useBrand();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [bf, setBf] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [requests, setRequests] = useState([]);
  const [reqBusy, setReqBusy] = useState('');
  const isOwner = user?.role === 'owner';

  const loadRequests = useCallback(async () => { try { const r = await api.get('/admin/access-requests'); setRequests(r.data || []); } catch { setRequests([]); } }, []);
  useEffect(() => { loadRequests(); }, [loadRequests]);
  const approveReq = async (rq, role = 'member') => {
    setReqBusy(rq.id);
    try { const r = await api.post(`/admin/access-requests/${rq.id}/approve`, { role }); toast({ title: 'Invite sent', description: r.data.email_sent ? `${rq.email} received an invitation email.` : `${rq.email} now has access (email not sent).` }); loadRequests(); load(); }
    catch (e) { toast({ title: 'Approve failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); } finally { setReqBusy(''); }
  };
  const dismissReq = async (rq) => { try { await api.delete(`/admin/access-requests/${rq.id}`); loadRequests(); toast({ title: 'Request dismissed' }); } catch { toast({ title: 'Failed', variant: 'destructive' }); } };

  useEffect(() => { if (brand?.loaded && !bf) setBf({ facilityName: brand.facilityName || '', logo: brand.logo || '', logoOriginal: brand.logoOriginal || brand.logo || '', logoTransparent: !!brand.logoTransparent, primaryColor: brand.primaryColor || '#0D9488', colors: brand.colors || [] }); }, [brand, bf]);

  const saveBrand = async () => {
    setSavingBrand(true);
    try { await api.put('/settings/brand', bf); await refreshBrand(); toast({ title: 'Brand saved', description: 'Your logo and colors now apply across the workspace.' }); }
    catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); } finally { setSavingBrand(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/admin/users'); setUsers(r.data || []); } catch { toast({ title: 'Failed to load users', variant: 'destructive' }); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim()) return; setInviting(true);
    try {
      const r = await api.post('/admin/invite', { email: email.trim(), role: inviteRole });
      if (r.data.email_sent) toast({ title: 'Invite sent', description: `An invitation email was sent to ${email}.` });
      else toast({ title: 'Access granted', description: `${email} can sign in. (Email not sent${r.data.email_error ? ': ' + r.data.email_error : ''}.)` });
      setEmail(''); load();
    }
    catch (e) { toast({ title: 'Invite failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); } finally { setInviting(false); }
  };
  const setStatus = async (u, status) => { try { await api.post(`/admin/users/${u.user_id}/status`, { status }); load(); toast({ title: `User ${status}` }); } catch (e) { toast({ title: 'Action failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); } };
  const setRole = async (u, role) => { try { await api.post(`/admin/users/${u.user_id}/role`, { role }); load(); toast({ title: 'Role updated' }); } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); } };
  const remove = async (u) => { try { await api.delete(`/admin/users/${u.user_id}`); load(); toast({ title: 'Access revoked' }); } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); } };

  const pending = users.filter((u) => u.status === 'pending');
  const others = users.filter((u) => u.status !== 'pending');

  const Row = ({ u }) => {
    const RI = roleIcon[u.role] || User;
    return (
      <div className="flex items-center gap-3 p-3 border rounded-lg">
        <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-semibold text-sm shrink-0">{(u.name || u.email || '?').charAt(0).toUpperCase()}</div>
        <div className="flex-1 min-w-0"><div className="font-medium text-sm truncate flex items-center gap-1">{u.name || u.email}{u.invited && !u.name && <span className="text-[10px] text-gray-400">(invited)</span>}</div><div className="text-xs text-gray-400 truncate">{u.email} · {u.doc_count} docs</div></div>
        <Badge className={`${statusStyle[u.status]} border-0 capitalize`}>{u.status}</Badge>
        <span className="text-xs text-gray-500 capitalize flex items-center gap-1 w-20"><RI className="w-3.5 h-3.5" />{u.role}</span>
        <div className="flex gap-1 shrink-0">
          {u.role !== 'owner' && (<>
            {u.status === 'pending' && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => setStatus(u, 'active')}><Check className="w-4 h-4" /></Button>}
            {u.status === 'active' && <Button size="sm" variant="outline" className="h-8 text-amber-600" onClick={() => setStatus(u, 'blocked')}><X className="w-4 h-4" /></Button>}
            {u.status === 'blocked' && <Button size="sm" variant="outline" className="h-8 text-emerald-600" onClick={() => setStatus(u, 'active')}><Check className="w-4 h-4" /></Button>}
            {isOwner && u.status === 'active' && <select value={u.role} onChange={(e) => setRole(u, e.target.value)} className="text-xs border rounded h-8 px-1"><option value="member">Member</option><option value="admin">Admin</option></select>}
            <Button size="sm" variant="ghost" className="h-8 text-red-500" onClick={() => remove(u)}><Trash2 className="w-4 h-4" /></Button>
          </>)}
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6"><div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-white" /></div><div><h1 className="text-2xl font-bold text-gray-900">Access Management</h1><p className="text-sm text-gray-500">Invite people and control who can use the workspace.</p></div></div>

        <Card className="mb-6"><CardContent className="p-4">
          <label className="text-sm font-medium flex items-center gap-1 mb-2"><Mail className="w-4 h-4" /> Invite someone by email</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input type="email" placeholder="name@hospital.org" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && invite()} />
            {isOwner && <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="border rounded-lg px-3 text-sm"><option value="member">Member</option><option value="admin">Admin</option></select>}
            <Button onClick={invite} disabled={inviting} className="bg-teal-600 hover:bg-teal-700">{inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Invite'}</Button>
          </div>
          <p className="text-xs text-gray-400 mt-2">Invited people get instant access the first time they sign in with Google.</p>
        </CardContent></Card>

        <Card className="mb-6"><CardContent className="p-4">
          <label className="text-sm font-medium flex items-center gap-1 mb-3"><PaletteIcon className="w-4 h-4" /> Workspace Brand</label>
          {bf && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Facility / Hospital Name</label>
                  <Input value={bf.facilityName} onChange={(e) => setBf({ ...bf, facilityName: e.target.value })} placeholder="e.g. Encompass Rehab Hospital" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Hospital Logo</label>
                  <div className="mt-1"><LogoUpload value={bf} onChange={(p) => setBf((s) => ({ ...s, ...p }))} /></div>
                  <p className="text-[10px] text-gray-400 mt-1">White backgrounds are removed automatically so the logo sits cleanly on colored headers.</p>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500">Brand Colors (first is the default accent)</label>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {bf.colors.map((c, i) => (
                    <div key={i} className="relative group">
                      <input type="color" value={c} onChange={(e) => { const cols = [...bf.colors]; cols[i] = e.target.value; setBf({ ...bf, colors: cols, primaryColor: i === 0 ? e.target.value : bf.primaryColor }); }} className="w-9 h-9 rounded-lg border cursor-pointer p-0" title="Click to edit" />
                      {i === 0 && <span className="absolute -top-2 -right-1 text-[8px] bg-teal-600 text-white rounded px-1">accent</span>}
                      <button onClick={() => { const cols = bf.colors.filter((_, j) => j !== i); setBf({ ...bf, colors: cols, primaryColor: cols[0] || bf.primaryColor }); }} className="absolute -bottom-1 -right-1 bg-white border rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <button onClick={() => setBf({ ...bf, colors: [...bf.colors, '#0D9488'] })} className="w-9 h-9 rounded-lg border-2 border-dashed text-gray-400 hover:border-teal-400 hover:text-teal-600">+</button>
                </div>
              </div>
              <Button onClick={saveBrand} disabled={savingBrand} className="bg-teal-600 hover:bg-teal-700">{savingBrand ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PaletteIcon className="w-4 h-4 mr-1" />} Save Brand</Button>
              <p className="text-xs text-gray-400">Your logo and colors become the default on new posters and documents across the workspace.</p>
            </div>
          )}
        </CardContent></Card>

        {requests.filter((r) => r.status !== 'approved').length > 0 && (
          <Card className="mb-6 border-cyan-200" data-testid="beta-requests-card"><CardContent className="p-4">
            <label className="text-sm font-medium flex items-center gap-1 mb-3"><Inbox className="w-4 h-4 text-cyan-600" /> Beta access requests ({requests.filter((r) => r.status !== 'approved').length})</label>
            <div className="space-y-2">
              {requests.filter((r) => r.status !== 'approved').map((rq) => (
                <div key={rq.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border border-cyan-500/30 rounded-lg" data-testid={`beta-request-${rq.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{rq.name} <span className="text-gray-400 font-normal">· {rq.email}</span></div>
                    <div className="text-xs text-gray-500 truncate">{[rq.organization, rq.role, rq.staffSize].filter(Boolean).join(' · ')}{rq.message ? ` — "${rq.message}"` : ''}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" disabled={reqBusy === rq.id} onClick={() => approveReq(rq, 'member')} data-testid={`beta-approve-${rq.id}`}>{reqBusy === rq.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> Approve & invite</>}</Button>
                    {isOwner && <Button size="sm" variant="outline" className="h-8" disabled={reqBusy === rq.id} onClick={() => approveReq(rq, 'admin')}>as Admin</Button>}
                    <Button size="sm" variant="ghost" className="h-8 text-red-500" onClick={() => dismissReq(rq)} data-testid={`beta-dismiss-${rq.id}`}><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        )}

        {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div> : (
          <>
            {pending.length > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
                <h2 className="text-sm font-semibold text-amber-700 flex items-center gap-1 mb-2"><Clock className="w-4 h-4" /> Pending Approval ({pending.length})</h2>
                <div className="space-y-2">{pending.map((u) => <Row key={u.user_id} u={u} />)}</div>
              </motion.div>
            )}
            <h2 className="text-sm font-semibold text-gray-700 mb-2">All Members ({others.length})</h2>
            <div className="space-y-2">{others.map((u) => <Row key={u.user_id} u={u} />)}</div>
          </>
        )}
      </div>
    </>
  );
}
